import { useContext } from 'react';

import { makeStyles } from '@mui/styles';
import TodayIcon from '@mui/icons-material/Today';

import CardCarousel from '@classify/react-product-components/CardCarousel';
import PathDiscoveryItem from '@classify/react-product-components/PathDiscoveryItem';
import Box from '@classify/react-core-components/Box';

import { useCourseRecommendedPaths } from '@openclassrooms/data-connect-web/learning-content';
import { translate } from '@openclassrooms/translations';
import { PathCardHorizontalSkeleton, PathCertification } from '@openclassrooms/ui-web/paths';
import { Path } from '@openclassrooms/sdk-web';
import { MetadataDuration } from '@openclassrooms/ui-web/learning-content';

import PathPromotionWrapper from './PathPromotionWrapper';
import CourseContext from '../../contexts/courseContext';

const useStyles = makeStyles(
  ({ breakpoints, spacing }) => ({
    pathCard: {
      width: '100%',
      marginTop: spacing(4),
      marginBottom: spacing(4),
      [breakpoints.up('md')]: {
        width: 868,
        marginTop: spacing(5),
      },
      '& a': {
        textDecoration: 'none',
        '&:hover': {
          color: 'inherit',
        },
      },
    },
  }),
  { classNamePrefix: 'PathPromotion' }
);

const PathPromotion = () => {
  const classes = useStyles();
  const { courseId, course } = useContext(CourseContext);
  const { category: categoryName, categoryColor, categoryId } = course;

  const courseCategory = {
    id: categoryId,
    name: categoryName,
    color: categoryColor,
  };

  const { data = {}, isLoading, error } = useCourseRecommendedPaths({ courseId });
  const { entities: paths = [] } = data;

  if (isLoading) {
    return (
      <PathPromotionWrapper category={courseCategory}>
        <PathCardHorizontalSkeleton className={classes.pathCard} />
      </PathPromotionWrapper>
    );
  }

  if (error || !paths || paths.length === 0) {
    return null;
  }
  const durationFullTimeMonths = (count: number) =>
    translate(
      'path_details.path_card.meta.full_time.months',
      { path_duration_months: count },
      count
    );
  return (
    <PathPromotionWrapper category={courseCategory}>
      <CardCarousel
        slidesWidth="lg"
        sliderProps={{
          previousArrowProps: {
            'data-event': 'Slider|Clicked::Previous|Paths recommended for you',
          },
          nextArrowProps: {
            'data-event': 'Slider|Clicked::Next|Paths recommended for you',
          },
        }}
        translations={{
          previousLabel: translate('global.word.go_back'),
          nextLabel: translate('global.word.go_to'),
        }}
      >
        {paths.map((pathItem: Path) => (
          <PathDiscoveryItem
            key={pathItem.title}
            hasForcedRowLayout
            link={pathItem.OpenClassroomsUrl}
            title={pathItem.title}
            field={pathItem.category.name}
            fieldColor={pathItem.category.color}
            description={pathItem.description}
            thumbnail={pathItem.illustration}
            data-event={`Slider|Clicked|Suggested paths::${pathItem.title}`}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {pathItem.certifications[0] && (
                <PathCertification
                  key="PathCertification"
                  translations={{
                    accessibilityLabel: translate('paths.accessibility_labels.diploma'),
                  }}
                >
                  {pathItem.certifications[0].marketingLabel}
                </PathCertification>
              )}
              {pathItem.duration && (
                <MetadataDuration
                  key="duration"
                  shouldNotWrap
                  duration={pathItem.duration}
                  translations={{
                    durationMonths: durationFullTimeMonths,
                  }}
                  icon={TodayIcon}
                />
              )}
            </Box>
          </PathDiscoveryItem>
        ))}
      </CardCarousel>
    </PathPromotionWrapper>
  );
};

export default PathPromotion;
